import { forwardRef, useImperativeHandle } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { Tabs } from 'antd';
import { useClassName, useConditionalPropertyForItemOfArray } from '../hooks';
import { withFieldWrapper } from '../fieldWrapper';
import { useElementDecorator } from '../FormRenderCtx';
import { LinkWrapper } from '../wrappers';
import { ConditionalPropertyPropType } from '../propTypes';

const NavTabsElement = forwardRef(function NavTabsElement(props, ref) {
  const { keyPath, id, className: classNameProp, style, componentProps } = props;

  const ElementDecorator = useElementDecorator();

  const { activeKey, centered, items: itemsProp, showUnderLine = false } = componentProps || {};

  const className = useClassName(classNameProp);

  const items = useConditionalPropertyForItemOfArray(itemsProp, 'hidden');

  useImperativeHandle(
    ref,
    () => ({
      node: null,
    }),
    [],
  );

  return (
    <ElementDecorator keyPath={keyPath} id={id}>
      <Tabs
        className={clsx(
          'nav-tabs-element form-element',
          { 'show-underline': showUnderLine },
          className,
        )}
        activeKey={activeKey}
        centered={centered}
        items={(items || [])
          .filter((item) => !item.hidden)
          .map((item) => {
            const { href, hrefIsSiteBased, suppressInheritIncludeDeleted } = item;
            return {
              key: item.key,
              label: (
                <LinkWrapper
                  componentProps={{
                    href,
                    hrefIsSiteBased,
                    suppressInheritIncludeDeleted,
                    replace: true,
                  }}
                >
                  {item.label}
                </LinkWrapper>
              ),
            };
          })}
        style={style}
      />
    </ElementDecorator>
  );
});

NavTabsElement.propTypes = {
  keyPath: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  id: PropTypes.string,
  className: PropTypes.string,
  componentProps: PropTypes.shape({
    /**
     * 当前激活的 Tab
     */
    activeKey: PropTypes.string,
    /**
     * 是否左右居中显示
     */
    centered: PropTypes.bool,
    /**
     * Tabs 的内容
     */
    items: PropTypes.arrayOf(
      PropTypes.shape({
        /**
         * Tab 的 key
         */
        key: PropTypes.string,
        /**
         * Tab 的名字
         */
        label: PropTypes.string,
        // icon: PropTypes.string, TODO
        /**
         * 链接地址
         */
        href: ConditionalPropertyPropType(PropTypes.string),
        /**
         * 如果值为 `false` 并且此链接位于 pbc 下，href 将自动拼接上 pbc 的 `token` 作为 `basename`，移动端将自动拼接上 `mobile` 的前缀
         * @default false
         */
        hrefIsSiteBased: PropTypes.bool,
        /**
         * 是否禁止自动继承当前页面的 `include_deleted` 属性，此属性一般在当前页面 url 的 search params 里
         * @default false
         */
        suppressInheritIncludeDeleted: PropTypes.bool,
        /**
         * 是否隐藏
         * @default false
         */
        hidden: ConditionalPropertyPropType(PropTypes.bool),
      }),
    ),
    /**
     * 底部是否显示边框
     */
    showUnderLine: PropTypes.bool,
  }),
};

// for @icp/utils/getComponentDisplayName, otherwise, in production mode, function name will be compressed.
NavTabsElement.displayName = 'NavTabs';

export default withFieldWrapper(NavTabsElement);
